/** @jsxImportSource ../simboluri-jsx */
import { ReactNode } from "react";
import { PAL_LAYERE } from "../paletaDesen";
import { ClasaElement, descrieClasa, descrieSimetria } from "../claseElemente";
import { FemGrinda, FemPereteBeton, FemResult, FemStalp, eLamela } from "../tipuri";
import Info from "../Info";
import { numar, t, tx, useT } from "../../i18n";
import { hashElement, hashSectiune, refCuNivel } from "../legaturi";
import { CULORI_CSS, MATERIAL_CHEIE, fundareaDesenata, goluriFlat, nr, parapeteFlat, pereteTurnatIn, radiereDesenate, turnatDinPerete, zidarieFlat } from "./model";
import type { Selectie } from "./model";

// ---------------------------------------------------------------------------
// Panoul de proprietăți (click pe element)
// ---------------------------------------------------------------------------

/** « (tip SA)», pentru capul panoului: tipul e primul lucru pe care îl caută
 *  ochiul la click, înaintea secțiunii — același tip ca în deviz și la
 *  «Evidențiază» (v. `claseElemente.ts`). Gol când piesa n-are încă o clasă
 *  (Pasul 19 n-a rulat). */
function tipulIn(
  clase: Record<string, ClasaElement> | undefined,
  id: string | null | undefined,
): string {
  const c = id ? clase?.[id] : undefined;
  return c?.clasa ? ` ${t("struct.fem.tip", { clasa: c.clasa })}` : "";
}

function RandSimetrie({ clase, id }: {
  clase?: Record<string, ClasaElement>;
  id?: string | null;
}) {
  // GRUPUL DE SIMETRIE: cu cine ar trebui să fie identică piesa asta —
  // oglinda ei față de axele clădirii și rândul fațadei la pas egal — și cât
  // din grup a ieșit chiar așa. Un grup respectat ia 10 % din calitatea
  // scorului, de aceea stă lângă clasă, nu într-un raport separat.
  const c = id ? clase?.[id] : undefined;
  const text = descrieSimetria(c);
  if (!text || !c?.simetrie) return null;
  const s = c.simetrie;
  const sub = s.respectata == null ? undefined
    : t(s.respectata ? "struct.fem.simetrie_respectata"
                     : "struct.fem.simetrie_nerespectata");
  return (
    <Row
      k={t("struct.fem.simetrie")}
      v={text}
      sub={sub}
      info={{
        titlu: t("struct.fem.simetrie_titlu"),
        ce: t("struct.fem.simetrie_ce"),
        deCe: t("struct.fem.simetrie_dece"),
      }}
    />
  );
}

function RandClasa({ clase, id }: {
  clase?: Record<string, ClasaElement>;
  id?: string | null;
}) {
  // CLASA de execuție: din ce tip de element face parte piesa asta. Douăzeci
  // de stâlpi identici sunt un cofraj refolosit de douăzeci de ori, iar al
  // douăzeci-și-unulea, cu 5 cm mai lat, e un cofraj nou — de aceea eticheta
  // stă lângă id, nu într-un raport separat (v. `claseElemente.ts`).
  const c = id ? clase?.[id] : undefined;
  const text = descrieClasa(c);
  if (!text) return null;
  return (
    <Row
      k={t("struct.fem.clasa")}
      v={text}
      sub={c?.eticheta || undefined}
      info={{
        titlu: t("struct.fem.clasa_titlu"),
        ce: t("struct.fem.clasa_ce"),
        deCe: t("struct.fem.clasa_dece"),
      }}
    />
  );
}


export function PanouProprietati({
  r,
  sel,
  onClose,
  projectId,
  clase,
}: {
  r: FemResult;
  sel: Selectie;
  onClose: () => void;
  // opțional: fără el panoul rămâne exact cum era, doar fără linkuri afară
  projectId?: string;
  /** clasa de execuție a fiecărui element, după id-ul lui de plan */
  clase?: Record<string, ClasaElement>;
}) {
  useT();
  if (!sel) {
    return (
      <div className="fem-panel sg-muted">
        <p>
          {t("struct.fem.gol_click", {
            grinda: t("struct.fem.gol_grinda"),
            stalp: t("struct.fem.gol_stalp") })}
        </p>
        {r.model.panouri?.length ? (
          <p>
            {tx("struct.fem.gol_click_ochi_placa")}
          </p>
        ) : null}
        <p>
          {t("struct.fem.gol_puncte_mici")}
        </p>
        {fundareaDesenata(r.model).grinzi.length ? (
          <p>
            {tx("struct.fem.gol_sub_cladire")}
          </p>
        ) : null}
      </div>
    );
  }

  const E_kpa = r.model.material.E_kpa;
  const fmt = (v: number, dig = 4) =>
    v >= 100 ? v.toFixed(1) : v >= 1 ? v.toFixed(2) : v.toPrecision(dig);

  if (sel.tip === "panou") {
    const p = (r.model.panouri ?? [])[sel.ref];
    if (!p) return null;
    const grosime = r.model.grosime_placa_m ?? 0;
    const e_placa = r.model.material.E_placa_kpa ?? r.model.material.E_kpa;
    const fis = r.parametri.rigiditate_analiza_placa;
    const lat = p.lx_m <= p.ly_m ? p.lx_m : p.ly_m;
    const lung = p.lx_m <= p.ly_m ? p.ly_m : p.lx_m;
    const raport = lat > 0 ? lung / lat : 0;
    const inchis = [
      p.pe_grinzi_pct
        ? t("struct.fem.p_inchis_grinzi", { n: p.pe_grinzi_pct }) : null,
      p.pe_pereti_pct
        ? t("struct.fem.p_inchis_pereti", { n: p.pe_pereti_pct }) : null,
      p.pe_contur_pct
        ? t("struct.fem.p_inchis_contur", { n: p.pe_contur_pct }) : null,
    ].filter(Boolean).join(" · ");
    // Ochiul unei VEDERI fără Pasul 7 n-are mesh: nici elemente finite, nici
    // arie atribuită de mesh, nici categorie de utilă (vin din celule).
    // Rândurile lor ar scrie «0 × ShellMITC4».
    const areMesh = p.nr_shells != null;
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: CULORI_CSS.placa }}>
            {t("struct.fem.p_ochi", { id: p.id })}
          </strong>
          <span className="sg-muted">
            {p.nivel_nume ?? t("struct.fem.p_nivel", { n: p.nivel })}
            {" · "}{nr(p.arie_m2, 1)} m²
          </span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={p.id}
              nivel={p.nivel_nume ?? t("struct.fem.p_nivel", { n: p.nivel })}
              sursa={t("struct.fem.gol_ochi")} />
            {areMesh && (
              <Row
                k={t("struct.fem.p_modelare")}
                v={`${p.nr_shells} × ShellMITC4`}
                sub={t("struct.fem.p_modelare_sub",
                       { cm: (grosime * 100).toFixed(0) })}
                info={{
                  titlu: t("struct.fem.p_shell_titlu"),
                  ce: t("struct.fem.p_shell_ce"),
                  deCe: t("struct.fem.p_shell_dece"),
                }}
              />
            )}
            <Row
              k={t("struct.fem.p_deschideri")}
              v={`${nr(lat, 2)} × ${nr(lung, 2)} m`}
              sub={t("struct.fem.p_deschideri_sub", {
                raport: nr(raport, 2),
                fel: t(raport < 2 ? "struct.fem.p_doua_directii"
                                  : "struct.fem.p_o_directie") })}
            />
            <Row
              k={t("struct.fem.p_deschidere_mica")}
              v={`${nr(p.deschidere_m ?? lat, 2)} m`}
              sub={t("struct.fem.p_deschidere_mica_sub")}
            />
            <Row
              k={t("struct.fem.p_suprafata")}
              v={`${nr(p.arie_m2, 2)} m²`}
              sub={areMesh
                ? t("struct.fem.p_suprafata_sub",
                    { mesh: nr(p.arie_mesh_m2 ?? 0, 2) })
                : undefined}
            />
            <Row k={t("struct.fem.p_grosime")}
              v={`${(grosime * 100).toFixed(0)} cm`}
              sub={t("struct.fem.p_grosime_sub")} />
            <Row
              k={t("struct.fem.p_contur")}
              v={inchis || "—"}
              sub={t("struct.fem.p_contur_sub")}
              info={{
                titlu: t("struct.fem.p_contur_titlu"),
                ce: t("struct.fem.p_contur_ce"),
                deCe: t("struct.fem.p_contur_dece"),
              }}
            />
            {p.cat != null && (
              <Row
                k={t("struct.fem.p_categorie")}
                v={t(p.cat === "C" ? "struct.fem.p_cat_c" : "struct.fem.p_cat_a")}
                sub={t("struct.fem.p_cat_sub", {
                  detaliu: p.arie_c_m2
                    ? t("struct.fem.p_cat_detaliu", { m2: nr(p.arie_c_m2, 1) })
                    : "" })}
              />
            )}
            <Row
              k={t("struct.fem.p_rigiditate")}
              v={`E = ${nr(e_placa / 1e6, 1)} GPa · ν = 0,2`}
              sub={fis && fis !== 1
                ? t("struct.fem.p_rig_fisurat", { fis })
                : t("struct.fem.p_rig_nefisurat")}
            />
          </tbody>
        </table>
        {/* Blocul „Cum e modelat în OpenSees" a fost SCOS din panouri
            : apelurile de solver nu au ce căuta în
            inspectorul de element — sunt de depanare, nu de citit. */}
      </div>
    );
  }

  if (sel.tip === "placa_pe_sol") {
    const ps = r.model.placa_pe_sol;
    if (!ps) return null;
    const el = ps.elemente?.[sel.ref];
    const ref = el?.element ?? "PS1";
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: CULORI_CSS.zidarie }}>
            {t("struct.fem.ps_titlu", { ref })}
          </strong>
          <span className="sg-muted">
            {t("struct.fem.ps_sub", {
              arie: (el?.arie_m2 ?? ps.arie_m2) ?? "—",
              cm: (ps.grosime_m * 100).toFixed(0),
              clasa: ps.clasa
                ? t("struct.fem.ps_clasa", { clasa: ps.clasa }) : "" })}
          </span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={ref} nivel={ps.nivel ?? undefined}
              sursa={t("struct.fem.ps_sursa")} />
            <Row
              k={t("struct.fem.ps_cote")}
              v={`${nr(ps.z_sus_m ?? 0, 2)} … ${nr(ps.z_jos_m ?? -ps.grosime_m, 2)} m`}
              sub={t("struct.fem.ps_cote_sub", {
                balast: ps.balast_m
                  ? t("struct.fem.ps_balast",
                      { cm: (ps.balast_m * 100).toFixed(0) }) : "" })}
            />
            <Row
              k={t("struct.fem.ps_modelare")}
              v={t("struct.fem.ps_rezemata")}
              sub={t("struct.fem.ps_rezemata_sub")}
              info={{
                titlu: t("struct.fem.ps_info_titlu"),
                ce: t("struct.fem.ps_info_ce"),
                deCe: t("struct.fem.ps_info_dece"),
              }}
            />
            {projectId && (
              <Row
                k={t("struct.fem.detalii")}
                v={
                  <span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <a href={hashElement(projectId, ref, null)}>
                      {t("struct.fem.link_element", { ref })}
                    </a>
                    <a href={hashSectiune(projectId, "armare_fundatie", null,
                                          { ref })}>
                      {t("struct.fem.link_armare")}
                    </a>
                  </span>
                }
                sub={t("struct.fem.link_sub")}
              />
            )}
          </tbody>
        </table>
      </div>
    );
  }

  if (sel.tip === "radier") {
    const rad = radiereDesenate(r.model)[sel.ref];
    if (!rad) return null;
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: CULORI_CSS.fundatieLinie }}>
            {t("struct.fem.rad_titlu")}{rad.corp ? ` · ${rad.corp}` : ""}
          </strong>
          <span className="sg-muted">
            {t("struct.fem.rad_sub", {
              arie: rad.amprenta_m2 != null ? nr(rad.amprenta_m2, 0) : "—",
              cm: (rad.h_m * 100).toFixed(0) })}
          </span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={rad.corp ? t("struct.fem.rad_id_corp", { corp: rad.corp }) : "RADIER"}
              sursa={t("struct.fem.rad_sursa")} />
            <Row
              k={t("struct.fem.ps_cote")}
              v={`${nr(rad.z_sus_m, 2)} … ${nr(rad.z_jos_m, 2)} m`}
              sub={t("struct.fem.ps_cote_sub", {
                balast: rad.balast_m
                  ? t("struct.fem.ps_balast",
                      { cm: (rad.balast_m * 100).toFixed(0) }) : "" })}
            />
            <Row
              k={t("struct.fem.ps_modelare")}
              v={t("struct.fem.rad_modelare")}
              sub={t("struct.fem.rad_modelare_sub")}
            />
            {!rad.corp && projectId && (
              <Row
                k={t("struct.fem.detalii")}
                v={
                  <a href={hashSectiune(projectId, "armare_fundatie", null)}>
                    {t("struct.fem.link_armare")}
                  </a>
                }
                sub={t("struct.fem.link_sub")}
              />
            )}
          </tbody>
        </table>
      </div>
    );
  }

  if (sel.tip === "fundatie") {
    const fund = fundareaDesenata(r.model);
    const g = fund.grinzi[sel.ref];
    if (!g) return null;
    const f = fund.fnd;
    const pe = f.pe_ax?.[g.ax];
    const tronsoane = fund.grinzi.filter((x) => x.ax === g.ax);
    const lungimeAx = tronsoane.reduce((t, x) => t + (x.L_m ?? 0), 0);
    const noduriAx = new Set<number>();
    tronsoane.forEach((x) => {
      noduriAx.add(x.nod_i);
      noduriAx.add(x.nod_j);
    });
    const resAx = (r.model.resoarte ?? []).filter((x) => noduriAx.has(x.nod));
    const kv = resAx.map((x) => x.kv_kn_m);
    const sect = { ...(f.sectiune ?? {}) };
    if (pe?.A_m2) sect.A_m2 = pe.A_m2;
    if (pe?.Iy_m4) sect.Iy_m4 = pe.Iy_m4;
    const bAx = g.B_m ?? pe?.B_m ?? f.B_m;
    const hSus = f.h_peste_m ?? 0.3;
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: CULORI_CSS.fundatieLinie }}>
            {t("struct.fem.gf_titlu", { ax: g.ax })}
            {tipulIn(clase, g.ax)}
          </strong>
          <span className="sg-muted">
            {t("struct.fem.gf_sub", {
              n: tronsoane.length, l: nr(lungimeAx, 2),
              b: bAx ? t("struct.fem.gf_b", { b: nr(bAx, 2) }) : "",
              afara: fund.inModel ? "" : t("struct.fem.gf_afara") })}
          </span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={g.ax}
              sursa={t("struct.fem.gf_sursa", { n: tronsoane.length })} />
            <RandClasa clase={clase} id={g.ax} />
            <Row
              k={t("struct.fem.gf_sectiune")}
              v={pe?.descriere ?? f.detaliu_descriere ?? "—"}
              sub={t("struct.fem.gf_sectiune_sub", { detaliu: f.detaliu ?? "T" })}
              info={{
                titlu: t("struct.fem.gf_altfel_titlu"),
                ce: t("struct.fem.gf_altfel_ce"),
                deCe: t("struct.fem.gf_altfel_dece"),
              }}
            />
            <Row
              k={t("struct.fem.ps_cote")}
              v={t("struct.fem.gf_cote", {
                sus: nr(hSus, 2),
                df: nr(g.Df_m ?? pe?.Df_m ?? f.Df_m ?? 0, 2) })}
              sub={t("struct.fem.gf_cote_sub",
                     { cm: (hSus * 100).toFixed(0) })}
              info={{
                titlu: t("struct.fem.gf_bara_titlu"),
                ce: t("struct.fem.gf_bara_ce"),
                deCe: t("struct.fem.gf_bara_dece"),
              }}
            />
            {projectId && (
              <Row
                k={t("struct.fem.gf_armatura")}
                v={
                  <a
                    href={hashSectiune(projectId, "armare_fundatie", null, {
                      ref: `F-${g.ax}`,
                    })}
                  >
                    {t("struct.fem.gf_armatura_link", { ax: g.ax })}
                  </a>
                }
                sub={t("struct.fem.gf_armatura_sub")}
                info={{
                  titlu: t("struct.fem.gf_bare3d_titlu"),
                  ce: t("struct.fem.gf_bare3d_ce"),
                  deCe: t("struct.fem.gf_bare3d_dece"),
                }}
              />
            )}
            <Row
              k={t("struct.fem.p_modelare")}
              v={t("struct.fem.ebc_nefisurat")}
              sub={`A = ${sect.A_m2 ? fmt(sect.A_m2) : "—"} m² · I_y = ${sect.Iy_m4 ? fmt(sect.Iy_m4) : "—"} m⁴ · I_z = ${sect.Iz_m4 ? fmt(sect.Iz_m4) : "—"} m⁴ · J = ${sect.J_m4 ? fmt(sect.J_m4) : "—"} m⁴`}
              info={{
                titlu: t("struct.fem.gf_nefisurat_titlu"),
                ce: t("struct.fem.gf_nefisurat_ce"),
                deCe: t("struct.fem.gf_nefisurat_dece"),
              }}
            />
            <Row
              k={t("struct.fem.p_rigiditate")}
              v={`E = ${nr(E_kpa / 1e6, 1)} GPa`}
              sub={sect.Iy_m4
                ? t("struct.fem.gf_ei",
                    { ei: fmt(E_kpa * sect.Iy_m4 / 1e3) })
                : undefined}
            />
            <Row
              k={t("struct.fem.gf_resoarte")}
              v={kv.length
                ? t("struct.fem.gf_resoarte_v", {
                    n: resAx.length, min: fmt(Math.min(...kv)),
                    max: fmt(Math.max(...kv)) })
                : "—"}
              sub={t("struct.fem.gf_resoarte_sub", {
                ks: f.ks_dan_cm3 ?? "—", sursa: f.ks_sursa ?? "—",
                pas: f.pas_resoarte_m ?? "—",
                raport: f.raport_orizontal ?? 0.5 })}
              info={{
                titlu: t("struct.fem.gf_resort_titlu"),
                ce: t("struct.fem.gf_resort_ce"),
                deCe: t("struct.fem.gf_resort_dece",
                        { lambda: f.lungime_elastica_m ?? "—" })
                  + t(f.fara_intindere
                    ? "struct.fem.gf_ent" : "struct.fem.gf_liniar"),
              }}
            />
            <Row
              k={t("struct.fem.gf_greutate")}
              v={`${pe?.q_kn_ml ?? f.q_kn_ml ?? "—"} kN/ml`}
              sub={t("struct.fem.gf_greutate_sub", {
                t: f.masa_infrastructura_t ?? "—",
                pct: f.masa_infrastructura_pct ?? "—",
                verb: t(f.masa_infrastructura_seismica
                  ? "struct.fem.gf_intra" : "struct.fem.gf_nu_intra") })}
              info={{
                titlu: t("struct.fem.gf_masa_titlu"),
                ce: t("struct.fem.gf_masa_ce"),
                deCe: t("struct.fem.gf_masa_dece"),
              }}
            />
          </tbody>
        </table>
      </div>
    );
  }

  if (sel.tip === "perete_zidarie") {
    const w = zidarieFlat(r.model)[sel.ref];
    if (!w) return null;
    // MATERIALUL, ca rând propriu: layerul spune ce ROL are peretele
    // (nestructural), nu din ce e făcut. Când arhitectura îl declară se
    // scrie declarația; când n-o declară — cazul oricărui proiect importat
    // din DWG, unde planșa nu scrie materialul nicăieri — se scrie zidăria
    // PRESUPUSĂ, spunând că e presupusă.
    const cheieMat = MATERIAL_CHEIE[w.material as keyof typeof MATERIAL_CHEIE];
    const turnat = turnatDinPerete(r.model, w.id_plan, w.nivel);
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          {/* titlul spune CE E piesa, nu doar că e „un perete": în 3D, un perete gri și unul de beton arată la fel
              la click, iar materialul e tocmai ce le deosebește. */}
          <strong style={{ color: "#6b7280" }}>
            {t("struct.fem.zid_titlu", { id: w.id_plan })}
          </strong>
          <span className="sg-muted">
            {w.nivel} · {t("struct.fem.zid_sub")}
          </span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={w.id_plan} nivel={w.nivel}
              sursa={t("struct.fem.zid_sursa")} />
            <Row
              k={t("prop.material")}
              v={cheieMat ? t(cheieMat) : t("prop.material.zidarie")}
              sub={t(cheieMat ? "struct.fem.zid_material_declarat"
                              : "struct.fem.zid_material_presupus")}
              info={{
                titlu: t("struct.fem.zid_material_titlu"),
                ce: t("struct.fem.zid_material_ce"),
                deCe: t("struct.fem.zid_material_dece"),
              }}
            />
            <Row k={t("struct.fem.sectiune")}
              v={t("struct.fem.zid_grosime", { cm: (w.t_m * 100).toFixed(0) })}
              sub={t("struct.fem.zid_grosime_sub")} />
            <Row k={t("struct.fem.lungime")} v={`${nr(w.L_m, 2)} m`} />
            <Row
              k={t("struct.fem.rol")}
              v={t("struct.fem.zid_rol")}
              sub={t("struct.fem.zid_rol_sub")}
              info={{
                titlu: t("struct.fem.zid_info_titlu"),
                ce: t("struct.fem.zid_info_ce"),
                deCe: t("struct.fem.zid_info_dece"),
              }}
            />
            {/* peretele rămas doar în parte: restul lui e beton, iar piesele
                care l-au turnat se numesc, ca lungimea din 3D să se poată
                împăca cu lungimea din Pasul 2 */}
            {turnat && (
              <Row
                k={t("struct.fem.zid_turnat")}
                v={t("struct.fem.zid_turnat_v", {
                  pct: turnat.pct,
                  piese: turnat.elemente.map((e) => e.id).join(", ") })}
                sub={t("struct.fem.zid_turnat_sub")}
              />
            )}
          </tbody>
        </table>
      </div>
    );
  }

  if (sel.tip === "fereastra" || sel.tip === "usa") {
    const g = goluriFlat(r.model, sel.tip)[sel.ref];
    if (!g) return null;
    const usa = sel.tip === "usa";
    const cotat = g.hp_m != null && g.h_m != null;
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: usa ? PAL_LAYERE.usi : PAL_LAYERE.ferestre }}>
            {t(usa ? "struct.fem.gol_usa" : "struct.fem.gol_fereastra")}{" "}
            {g.id_plan}
          </strong>
          <span className="sg-muted">
            {t("struct.fem.gol_sub", { nivel: g.nivel, l: nr(g.L_m, 2) })}
          </span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={g.id_plan} nivel={g.nivel}
              sursa={t("struct.fem.gol_sursa")} />
            <Row k={t("struct.fem.gol_latime")} v={`${nr(g.L_m, 2)} m`}
              sub={t("struct.fem.gol_latime_sub")} />
            <Row k={t("struct.fem.p_grosime")}
              v={`${(g.t_m * 100).toFixed(0)} cm`}
              sub={t("struct.fem.gol_grosime_sub")} />
            <Row
              k={t("struct.fem.gol_hp")}
              v={g.hp_m != null
                ? `${nr(g.hp_m, 2)} m` : t("struct.fem.gol_hp_necotat")}
              sub={t(g.hp_m != null
                ? "struct.fem.gol_hp_sub" : "struct.fem.gol_hp_fara")}
              info={{
                titlu: t("struct.fem.gol_hp_titlu"),
                ce: t("struct.fem.gol_hp_ce"),
                deCe: t("struct.fem.gol_hp_dece"),
              }}
            />
            <Row
              k={t("struct.fem.gol_h")}
              v={g.h_m != null
                ? `${nr(g.h_m, 2)} m` : t("struct.fem.gol_h_necunoscuta")}
              sub={g.h_m != null
                ? t("struct.fem.gol_h_sub", { m: nr(g.hp_m! + g.h_m, 2) })
                : t("struct.fem.gol_h_fara")}
              info={{
                titlu: t("struct.fem.gol_h_titlu"),
                ce: t("struct.fem.gol_h_ce"),
                deCe: t("struct.fem.gol_h_dece"),
              }}
            />
            {!cotat && (
              <Row
                k={t("struct.fem.gol_3d")}
                v={t("struct.fem.gol_3d_v")}
                sub={t("struct.fem.gol_3d_sub")}
              />
            )}
            <Row k={t("struct.fem.gol_gazda")} v={g.perete || "—"}
              sub={t(g.perete ? "struct.fem.gol_gazda_sub"
                              : "struct.fem.gol_gazda_fara")} />
            <Row
              k={t("struct.fem.rol")}
              v={t("struct.fem.gol_rol")}
              sub={t("struct.fem.gol_rol_sub")}
            />
          </tbody>
        </table>
      </div>
    );
  }

  if (sel.tip === "parapet") {
    const m = parapeteFlat(r.model)[sel.ref];
    if (!m) return null;
    const cheieMatPar =
      MATERIAL_CHEIE[m.material as keyof typeof MATERIAL_CHEIE];
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: PAL_LAYERE.margine }}>{t("struct.fem.parapet_titlu", { id: m.id_plan })}</strong>
          <span className="sg-muted">{m.nivel} · {t("struct.fem.nestructural")}</span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={m.id_plan} nivel={m.nivel}
              sursa={t("struct.fem.par_sursa")} />
            {/* parapetul e tot un perete nestructural: aceeași întrebare, în
                același loc din panou */}
            <Row
              k={t("prop.material")}
              v={cheieMatPar ? t(cheieMatPar) : t("prop.material.zidarie")}
              sub={t(cheieMatPar ? "struct.fem.zid_material_declarat"
                                 : "struct.fem.zid_material_presupus")}
            />
            <Row k={t("struct.fem.lungime")} v={`${nr(m.L_m, 2)} m`} />
            <Row k={t("struct.fem.p_grosime")}
              v={`${(m.t_m * 100).toFixed(0)} cm`}
              sub={t("struct.fem.par_grosime_sub")} />
            <Row
              k={t("struct.fem.inaltime")}
              v={m.h_m != null
                ? t(m.inaltime_presupusa
                    ? "struct.fem.par_h_presupusa" : "struct.fem.par_h",
                    { m: nr(m.h_m, 2) })
                : t("struct.fem.par_h_necotata")}
              sub={m.h_m != null
                ? t(m.inaltime_presupusa
                  ? "struct.fem.par_h_sub_presupus"
                  : "struct.fem.par_h_sub_dosar")
                : t("struct.fem.par_h_sub_vechi")}
              info={{
                titlu: t("struct.fem.par_h_titlu"),
                ce: t("struct.fem.par_h_ce"),
                deCe: t("struct.fem.par_h_dece"),
              }}
            />
            <Row
              k={t("struct.fem.rol")}
              v={t("struct.fem.par_rol")}
              sub={t("struct.fem.par_rol_sub")}
            />
          </tbody>
        </table>
      </div>
    );
  }

  if (sel.tip === "zidarie_confinata") {
    const p: FemPereteBeton = (r.model.pereti_zidarie ?? [])[sel.ref];
    if (!p) return null;
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: CULORI_CSS.zidarieConfinata }}>
            {t("struct.fem.zc_titlu", { id: p.id_plan })}
          </strong>
          <span className="sg-muted">
            {t("struct.fem.pb_sub", { nivel: p.nivel, l: p.L_m })}
          </span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={p.id_plan} nivel={p.nivel}
              sursa={t("struct.fem.zc_sursa")} />
            <Row k={t("prop.material")} v={t("struct.fem.zc_material")}
              sub={t("struct.fem.zc_material_sub")} />
            <Row k={t("struct.fem.pb_intre_stalpi")}
              v={`${p.stalp_i ?? "—"} – ${p.stalp_j ?? "—"}`}
              sub={t("struct.fem.zc_capete_sub")} />
            <Row
              k={t("struct.fem.sectiune")}
              v={`${(p.t_m * 100).toFixed(0)} × ${(p.h_web_m * 100).toFixed(0)} cm`}
              sub={t("struct.fem.zc_sect_sub")}
              info={{
                titlu: t("struct.fem.zc_info_titlu"),
                ce: t("struct.fem.zc_info_ce"),
                deCe: t("struct.fem.zc_info_dece"),
              }}
            />
            <Row k={t("struct.fem.zc_greutate")}
              v={`${p.q_kn_m.toFixed(1)} kN/m`}
              sub={t("struct.fem.zc_greutate_sub")} />
          </tbody>
        </table>
      </div>
    );
  }

  if (sel.tip === "perete_beton") {
    const p: FemPereteBeton = (r.model.pereti_beton ?? [])[sel.ref];
    if (!p) return null;
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: CULORI_CSS.pereteBeton }}>
            {t("struct.fem.pb_titlu", { id: p.id_plan })}
          </strong>
          <span className="sg-muted">
            {t("struct.fem.pb_sub", { nivel: p.nivel, l: p.L_m })}
          </span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={p.id_plan} nivel={p.nivel}
              sursa={t(p.tip === "balcon" ? "struct.fem.pb_sursa_balcon"
                                          : "struct.fem.pb_sursa_subsol")} />
            {/* materialul, pe același rând ca la peretele nestructural: la
                click, două piese care arată la fel trebuie să răspundă la
                aceeași întrebare, nu una la ea și cealaltă la alta */}
            <Row k={t("prop.material")}
              v={t("struct.fem.beton_clasa",
                   { clasa: r.model.material.beton })}
              sub={t("struct.fem.beton_material_sub")} />
            <Row
              k={t(p.tip === "balcon" ? "struct.fem.pb_amplasare"
                                      : "struct.fem.pb_intre_stalpi")}
              v={p.tip === "balcon"
                ? t("struct.fem.pb_pe_balcon")
                : `${p.stalp_i ?? "?"} – ${p.stalp_j ?? "?"}`}
              sub={p.tip === "balcon"
                ? t("struct.fem.pb_balcon_sub", {
                    arh: p.id_arh
                      ? t("struct.fem.pb_balcon_arh", { id: p.id_arh }) : "" })
                : undefined}
            />
            <Row
              k={t("struct.fem.sectiune")}
              v={`${(p.t_m * 100).toFixed(0)} × ${(p.h_web_m * 100).toFixed(0)} cm`}
              sub={t(p.tip === "balcon" ? "struct.fem.pb_sect_balcon"
                                        : "struct.fem.pb_sect_subsol")}
              info={p.tip === "balcon" ? {
                titlu: t("struct.fem.pb_balcon_titlu"),
                ce: t("struct.fem.pb_balcon_ce"),
                deCe: t("struct.fem.pb_balcon_dece"),
              } : {
                titlu: t("struct.fem.pb_subsol_titlu"),
                ce: t("struct.fem.pb_subsol_ce"),
                deCe: t("struct.fem.pb_subsol_dece"),
              }}
            />
            {p.tip !== "balcon" && (
              <Row
                k={t("struct.fem.pb_pozitie")}
                v={p.e_fata_m > 0
                  ? t("struct.fem.pb_fata",
                      { cm: (p.e_fata_m * 100).toFixed(0) })
                  : t("struct.fem.pb_centrat")}
                sub={t("struct.fem.pb_pozitie_sub")}
              />
            )}
            {p.tip !== "balcon" && p.z_jos_m != null && (
              <Row
                k={t("struct.fem.pb_reazem")}
                v={p.rezemare === "fundatie"
                  ? t("struct.fem.pb_pe_fundatie", {
                      ax: p.ax_fundatie
                        ? t("struct.fem.pb_ax", { ax: p.ax_fundatie }) : "",
                      z: nr(p.z_jos_m, 2) })
                  : p.rezemare === "placa_pe_sol"
                    ? t("struct.fem.pb_pe_placa_sol", { z: nr(p.z_jos_m, 2) })
                    : p.rezemare === "perete"
                      ? t("struct.fem.pb_pe_perete", { z: nr(p.z_jos_m, 2) })
                      : p.rezemare === "placa_nivel"
                        ? t("struct.fem.pb_pe_placa_nivel", { z: nr(p.z_jos_m, 2) })
                        : t("struct.fem.pb_pe_cota", { z: nr(p.z_jos_m, 2) })}
                sub={p.h_desen_m != null && p.h_desen_m > p.h_web_m + 0.005
                  ? t("struct.fem.pb_turnat", {
                      h: nr(p.h_desen_m, 2),
                      ce: t(p.grinda_deasupra
                        ? "struct.fem.pb_grinzii" : "struct.fem.pb_placii"),
                      calcul: nr(p.h_web_m, 2),
                      dif: ((p.h_desen_m - p.h_web_m) * 100).toFixed(0) })
                  : t("struct.fem.pb_nu_pluteste")}
              />
            )}
            <Row k={t("struct.fem.pb_greutate")} v={`${nr(p.q_kn_m, 1)} kN/m`}
              sub={t("struct.fem.pb_greutate_sub")} />
            {p.tip !== "balcon" && p.perimetral != null && (
              <Row k={t("struct.fem.pb_amplasare")}
                v={t(p.perimetral ? "struct.fem.pb_perimetral"
                                  : "struct.fem.pb_interior")}
                sub={p.perimetral && p.t_m < 0.25
                  ? t("struct.fem.pb_perimetral_sub") : undefined} />
            )}
          </tbody>
        </table>
        <div className="sg-muted fem-note">{t("struct.fem.pb_nota")}</div>
      </div>
    );
  }

  if (sel.tip === "stalp") {
    const s: FemStalp = r.model.stalpi[sel.ref];
    const f = r.parametri.rigiditate_analiza_stalpi;
    // la stâlpul cu brațe, aria și inerția sunt ale poligonului întreg
    const A = s.A_m2 ?? s.b_m * s.h_m;
    const I = s.I_dir_m4 ?? (s.b_m * Math.pow(s.h_m, 3)) / 12;
    // înălțimea reală a etajului stâlpului = diferența de cotă a nodurilor
    const nd = new Map(r.model.noduri.map((n) => [n.id, n]));
    const hEtaj =
      (nd.get(s.nod_sus)?.z_m ?? 0) - (nd.get(s.nod_jos)?.z_m ?? 0) ||
      r.parametri.inaltime_etaj_m;
    const idSt = refCuNivel(s.id_plan, s.etaj);
    // peretele Pasului 2 peste care s-a turnat piesa asta, dacă e unul: la
    // lamela din zidul unui etaj retras, ea E peretele — iar peretele lipsește
    // din layerul nestructural tocmai fiindcă e aici
    const pesteZid = pereteTurnatIn(r.model, idSt);
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          <strong style={{ color: CULORI_CSS.stalp }}>
            {t(eLamela(s)
              ? "struct.fem.st_lamela" : "struct.fem.st_stalp")}{" "}
            {refCuNivel(s.id_plan, s.etaj) ?? `#${sel.ref + 1}`}
            {tipulIn(clase, idSt)}
          </strong>
          <span className="sg-muted">{s.nivel}</span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={idSt ?? `#${sel.ref + 1}`}
              nivel={s.nivel} sursa={t("struct.fem.st_sursa")} />
            <RandClasa clase={clase} id={idSt} />
            <RandSimetrie clase={clase} id={idSt} />
            <Row k={t("prop.material")}
              v={t("struct.fem.beton_clasa",
                   { clasa: r.model.material.beton })}
              sub={t("struct.fem.beton_material_sub")} />
            <Row k={t("struct.fem.sectiune")}
              v={`${(s.b_m * 100).toFixed(0)} × ${(s.h_m * 100).toFixed(0)} cm`} />
            {(s.brate?.length ?? 0) > 0 && (
              <Row k={t("struct.fem.brate")}
                v={s.brate!.map((b) => t("struct.fem.brate_v", {
                  n: (b.b_m * 100).toFixed(0), h: (b.h_m * 100).toFixed(0),
                  // brațul stă pe axa cealaltă decât latura lungă `dir`
                  axa: Math.abs(s.dir?.[0] ?? 1) >= Math.abs(s.dir?.[1] ?? 0) ? "Y" : "X",
                })).join(" · ")}
                sub={t("struct.fem.brate_sub")} />
            )}
            {pesteZid && (
              <Row
                k={t("struct.fem.st_peste_zid")}
                v={t("struct.fem.st_peste_zid_v", {
                  id: pesteZid.zid.id_plan, pct: pesteZid.pct })}
                sub={t("struct.fem.st_peste_zid_sub")}
                info={{
                  titlu: t("struct.fem.st_peste_zid_titlu"),
                  ce: t("struct.fem.st_peste_zid_ce"),
                  deCe: t("struct.fem.st_peste_zid_dece"),
                }}
              />
            )}
            <Row
              k={t("struct.fem.st_h_etaj")}
              v={`${nr(hEtaj, 2)} m`}
              sub={t("struct.fem.st_h_etaj_sub")}
            />
            <Row k="A" v={`${fmt(A)} m²`} />
            <Row k={t("struct.fem.st_i_brut")}
              v={`${nr(I * 1e4, 1)}·10⁻⁴ m⁴`} />
            <Row k={t("struct.fem.st_i_fisurat", { f })}
              v={`${nr(f * I * 1e4, 1)}·10⁻⁴ m⁴`} />
            <Row k={t("struct.fem.st_ei_fisurat")}
              v={`${numar(Math.round(f * I * E_kpa))} kN·m²`} />
            <Row k={t("struct.fem.st_greutate")}
              v={`${fmt(A * hEtaj * 25)} kN`}
              sub={t("struct.fem.st_greutate_sub")} />
            <Row k={t("struct.fem.st_rigid_sus")}
              v={t("struct.fem.st_rigid_sus_v")} />
          </tbody>
        </table>
        <div className="sg-muted fem-note">
          {t("struct.fem.st_nota", { f })}
        </div>
      </div>
    );
  }

  const g: FemGrinda = r.model.grinzi[sel.ref];
  const f = r.parametri.rigiditate_analiza_grinzi;

  // varianta shell: grinda are doar inima sub placă + offsetul compozit
  if (g.sectiune_web) {
    const sw = g.sectiune_web;
    return (
      <div className="fem-panel">
        <div className="fem-panel-head">
          {/* id-ul de plan în capul panoului, ca la stâlpi — grinda se identifică din click, nu din desen */}
          <strong style={{ color: CULORI_CSS.grinda }}>
            {t("struct.fem.gr_titlu",
               { ref: refCuNivel(g.id_plan, g.etaj) ?? `#${sel.ref + 1}` })}
            {tipulIn(clase, refCuNivel(g.id_plan, g.etaj))}
          </strong>
          <span className="sg-muted">{g.nivel} · L = {g.L_m} m · {t("struct.fem.inima_sub_placa")}</span>
          <button className="fem-close" onClick={onClose}>×</button>
        </div>
        <table className="fem-props">
          <tbody>
            <RandId id={refCuNivel(g.id_plan, g.etaj) ?? `#${sel.ref + 1}`}
              nivel={g.nivel} sursa={t("struct.fem.gr_sursa")} />
            <RandClasa clase={clase} id={refCuNivel(g.id_plan, g.etaj)} />
            <Row
              k={t("struct.fem.gr_inima")}
              v={`${(g.bw_m * 100).toFixed(0)} × ${((g.h_web_m ?? 0) * 100).toFixed(0)} cm`}
              sub={t("struct.fem.gr_inima_sub", {
                bw: (g.bw_m * 100).toFixed(0), h: (g.h_m * 100).toFixed(0),
                hf: (g.hf_m * 100).toFixed(0) })}
              info={{
                titlu: t("struct.fem.gr_inima_titlu"),
                ce: t("struct.fem.gr_inima_ce"),
                deCe: t("struct.fem.gr_inima_dece"),
              }}
            />
            <Row k={t("struct.fem.gr_offset")}
              v={`${nr((g.e_offset_m ?? 0) * 100, 1)} cm`}
              sub={t("struct.fem.gr_offset_sub")} />
            <Row k={t("struct.fem.gr_a_inima")} v={`${fmt(sw.A_m2)} m²`} />
            <Row k={t("struct.fem.gr_i_inima")}
              v={`${nr(sw.I_puternic_m4 * 1e4, 2)}·10⁻⁴ m⁴`} />
            <Row k={t("struct.fem.st_i_fisurat", { f })}
              v={`${nr(f * sw.I_puternic_m4 * 1e4, 2)}·10⁻⁴ m⁴`} />
            <Row k={t("struct.fem.st_ei_fisurat")}
              v={`${numar(Math.round(f * sw.I_puternic_m4 * E_kpa))} kN·m²`} />
            <Row k={t("struct.fem.gr_q_inima")}
              v={`${nr(g.q_web_kn_m ?? 0, 1)} kN/m`}
              sub={t("struct.fem.gr_q_inima_sub")} />
            <Row k={t("struct.fem.gr_noduri_placa")}
              v={t("struct.fem.gr_noduri_placa_v", { n: g.noduri.length })} />
            <Row k={t("struct.fem.gr_zone_rigide")}
              v={t("struct.fem.gr_zone_rigide_v")} />
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="fem-panel">
      <div className="fem-panel-head">
        <strong style={{ color: CULORI_CSS.grinda }}>
          {t("struct.fem.gr_t",
             { ref: refCuNivel(g.id_plan, g.etaj) ?? `#${sel.ref + 1}` })}
          {tipulIn(clase, refCuNivel(g.id_plan, g.etaj))}
        </strong>
        <span className="sg-muted">
          {t("struct.fem.gr_sub", { nivel: g.nivel, l: g.L_m })}
        </span>
        <button className="fem-close" onClick={onClose}>×</button>
      </div>
      <SectiuneT g={g} />
      <table className="fem-props">
        <tbody>
          <RandId id={refCuNivel(g.id_plan, g.etaj) ?? `#${sel.ref + 1}`}
            nivel={g.nivel} sursa={t("struct.fem.gr_sursa")} />
          <RandClasa clase={clase} id={refCuNivel(g.id_plan, g.etaj)} />
          <Row
            k="b_eff"
            v={`${(g.b_eff_m! * 100).toFixed(0)} cm`}
            info={{
              titlu: t("struct.fem.gr_beff_titlu"),
              ce: t("struct.fem.gr_beff_ce"),
              deCe: t("struct.fem.gr_beff_dece"),
            }}
          />
          <Row k={t("struct.fem.gr_talpa")} v={`${(g.b_eff_laturi_m![0] * 100).toFixed(0)} / ${(g.b_eff_laturi_m![1] * 100).toFixed(0)} cm`} />
          <Row k="l₀"
            v={t("struct.fem.gr_l0",
                 { l: String(g.l0_m ?? "—"),
                   tip: String(g.tip_deschidere ?? "—") })} />
          <Row k={t("struct.fem.gr_a_t")} v={`${fmt(g.sectiune_T!.A_m2)} m²`} />
          <Row
            k={t("struct.fem.gr_i_t")}
            v={`${nr(g.sectiune_T!.I_puternic_m4 * 1e4, 1)}·10⁻⁴ m⁴`}
            sub={t("struct.fem.gr_i_t_sub", {
              brut: nr(g.sectiune_bruta!.I_puternic_m4 * 1e4, 1),
              plus: Math.round((g.sectiune_T!.I_puternic_m4
                / g.sectiune_bruta!.I_puternic_m4 - 1) * 100) })}
          />
          <Row k={t("struct.fem.st_i_fisurat", { f })}
            v={`${nr(f * g.sectiune_T!.I_puternic_m4 * 1e4, 1)}·10⁻⁴ m⁴`} />
          <Row k={t("struct.fem.st_ei_fisurat")}
            v={`${numar(Math.round(f * g.sectiune_T!.I_puternic_m4 * E_kpa))} kN·m²`} />
          <Row k={t("struct.fem.gr_tributara")}
            v={`${g.latime_tributara_m} m`} />
          <Row
            k={t("struct.fem.gr_q_permanent")}
            v={`${nr(g.q_dead_kn_m! + g.q_sdl_kn_m!, 1)} kN/m`}
            sub={t("struct.fem.gr_q_permanent_sub",
                   { dead: g.q_dead_kn_m!, sdl: g.q_sdl_kn_m! })}
          />
          <Row k={t("struct.fem.gr_q_util")}
            v={`${nr(g.q_live_kn_m!, 1)} kN/m`} />
          <Row k={t("struct.fem.gr_noduri_fem")}
            v={t("struct.fem.gr_noduri_fem_v", {
              n: g.noduri.length - 2, seg: g.element_ids.length })} />
          <Row k={t("struct.fem.gr_zone_rigide")}
            v={t("struct.fem.gr_zone_rigide_v")} />
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inspectorul OpenSees: CUM e modelat elementul selectat
// ---------------------------------------------------------------------------
// Cerința lui Stefan: la clic pe un element, proiectantul
// trebuie să poată verifica reprezentarea, nu doar secțiunea. Argumentele de
// mai jos vin din `model.elemente[].opensees` / `model.sectiuni_shell[]`,
// scrise de `fem_shell.descrie_opensees` cu ACELEAȘI funcții cu care se
// cheamă ops.element(...) — deci ce se vede aici e ce rulează în solver.

// Nu există blocuri «Cum e modelat în OpenSees» în inspector: ar fi depanare
// livrată în produs.
// Modelul de solver se citește din codul solverului, nu din inspectorul de
// element.

function Row({
  k,
  v,
  sub,
  info,
}: {
  k: string;
  // valoarea poate fi și un nod (un link către pasul care armează elementul),
  // nu doar text
  v: ReactNode;
  sub?: string;
  info?: { titlu: string; ce: string; deCe: string };
}) {
  return (
    <tr>
      <td className="fem-k">
        {k}
        {info && <Info titlu={info.titlu} ce={info.ce} deCe={info.deCe} />}
      </td>
      <td>
        {v}
        {sub && <div className="sg-muted" style={{ fontSize: 12 }}>{sub}</div>}
      </td>
    </tr>
  );
}

// Id-ul piesei, ca PRIMUL rând al oricărui panou de proprietăți.
//
// Îl purta doar titlul („Peretele E43"), iar acolo se citește ca nume, nu ca
// identificator: nu se vede că e cheia sub care piesa apare în tabelele de
// eforturi, în verificări și în extrasul de armare, și nu se poate selecta ca
// să fie pus într-un mesaj («nu văd id pentru zidărie»).
// Un rând propriu, la fel la toate familiile, rezolvă și una și alta.
//
// `nivel` stă tot aici: același id se repetă pe niveluri (E43 e al etajului
// 2), deci id-ul singur nu identifică piesa în clădire, ci doar în plan.
function RandId({ id, nivel, sursa }: {
  id?: string | null;
  nivel?: string | null;
  sursa?: string;
}) {
  return (
    <Row
      k="Id"
      v={id || "—"}
      sub={[nivel, sursa].filter(Boolean).join(" · ") || undefined}
    />
  );
}

// Schița secțiunii T, desenată la scară.
function SectiuneT({ g }: { g: FemGrinda }) {
  const W = 240;
  const H = 130;
  const pad = 26;
  const beffm = g.b_eff_m ?? g.bw_m;
  const scale = Math.min((W - 2 * pad) / beffm, (H - 2 * pad) / g.h_m);
  const bw = g.bw_m * scale;
  const h = g.h_m * scale;
  const hf = g.hf_m * scale;
  const beff = beffm * scale;
  const x0 = (W - beff) / 2;
  const y0 = (H - h) / 2;
  const xw = (W - bw) / 2;
  return (
    <svg width={W} height={H} className="fem-sect">
      <path
        d={[
          `M ${x0} ${y0}`,
          `L ${x0 + beff} ${y0}`,
          `L ${x0 + beff} ${y0 + hf}`,
          `L ${xw + bw} ${y0 + hf}`,
          `L ${xw + bw} ${y0 + h}`,
          `L ${xw} ${y0 + h}`,
          `L ${xw} ${y0 + hf}`,
          `L ${x0} ${y0 + hf}`,
          "Z",
        ].join(" ")}
        fill={`${CULORI_CSS.grinda}33`}
        stroke={CULORI_CSS.grinda}
        strokeWidth="1.5"
      />
      {/* cote */}
      <line x1={x0} y1={y0 - 8} x2={x0 + beff} y2={y0 - 8} stroke="#737373" strokeWidth="0.7" />
      <text x={W / 2} y={y0 - 12} textAnchor="middle" fontSize="10" fill="#3a3a3a">
        b_eff = {(beffm * 100).toFixed(0)}
      </text>
      <line x1={x0 + beff + 8} y1={y0} x2={x0 + beff + 8} y2={y0 + hf} stroke="#737373" strokeWidth="0.7" />
      <text x={x0 + beff + 12} y={y0 + hf / 2 + 3} fontSize="10" fill="#3a3a3a">
        {(g.hf_m * 100).toFixed(0)}
      </text>
      <line x1={xw - 8} y1={y0} x2={xw - 8} y2={y0 + h} stroke="#737373" strokeWidth="0.7" />
      <text x={xw - 12} y={y0 + h / 2} textAnchor="end" fontSize="10" fill="#3a3a3a">
        h = {(g.h_m * 100).toFixed(0)}
      </text>
      <line x1={xw} y1={y0 + h + 8} x2={xw + bw} y2={y0 + h + 8} stroke="#737373" strokeWidth="0.7" />
      <text x={W / 2} y={y0 + h + 20} textAnchor="middle" fontSize="10" fill="#3a3a3a">
        bw = {(g.bw_m * 100).toFixed(0)} cm
      </text>
    </svg>
  );
}
